import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Stage, Layer } from 'react-konva';
import ShapeRenderer, { buildObjectFromDrag, extractSpatialProps, supportsKeyframes } from './shapes/ShapeRenderer';
import { makeObjectId, isObjectVisibleAt } from './hooks/useTelestrationScene';

/**
 * Konva stage overlayed on the video. Tracks the video's rendered box
 * via ResizeObserver so the stage matches exactly.
 */
export default function AnnotationStage({ videoRef, scene, sceneApi, currentTime, duration }) {
  const wrapperRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0, left: 0, top: 0 });
  const [drag, setDrag] = useState(null); // { start, end, tool }
  const [polygonPoints, setPolygonPoints] = useState(null); // [[x,y], ...] in %, while building

  // ── Track the rendered <video> box ─────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    const wrapper = wrapperRef.current;
    if (!video || !wrapper) return;

    const update = () => {
      const vr = video.getBoundingClientRect();
      const wr = wrapper.getBoundingClientRect();
      setBox({
        w: vr.width,
        h: vr.height,
        left: vr.left - wr.left,
        top: vr.top - wr.top,
      });
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(video);
    ro.observe(wrapper);

    video.addEventListener('loadedmetadata', update);
    window.addEventListener('resize', update);

    return () => {
      ro.disconnect();
      video.removeEventListener('loadedmetadata', update);
      window.removeEventListener('resize', update);
    };
  }, [videoRef]);

  const isDrawingTool = scene.activeTool !== 'select';
  const isPolygonTool = scene.activeTool === 'polygon';

  // Route drag-end updates: animatable types upsert a keyframe at currentTime;
  // non-animatable types keep the static update (replace props).
  const onSpatialChange = useCallback((id, partial) => {
    const obj = scene.objects.find((o) => o.id === id);
    if (!obj) return;
    if (supportsKeyframes(obj.type)) {
      // If this is the first motion on a previously static object, seed
      // a keyframe at tIn with its original position so the shape doesn't
      // teleport to the new position from the start.
      const hasKeyframes = obj.keyframes && obj.keyframes.length > 0;
      if (!hasKeyframes) {
        const initial = extractSpatialProps(obj.type, obj.props);
        if (initial) sceneApi.upsertKeyframe(id, obj.tIn ?? 0, initial);
      }
      sceneApi.upsertKeyframe(id, currentTime, partial);
    } else {
      sceneApi.updateObject(id, partial);
    }
  }, [scene.objects, sceneApi, currentTime]);

  // Reset polygon-in-progress when tool changes away
  useEffect(() => {
    if (!isPolygonTool) setPolygonPoints(null);
  }, [isPolygonTool]);

  // Commit polygon on Enter or double-click
  const commitPolygon = useCallback(() => {
    if (!polygonPoints || polygonPoints.length < 3) {
      setPolygonPoints(null);
      return;
    }
    const tIn = Math.max(0, currentTime);
    const tOut = Math.min(
      duration || currentTime + scene.defaultDuration,
      currentTime + scene.defaultDuration
    );
    const polyProps = {
      points: polygonPoints,
      stroke: scene.color,
      strokeWidth: scene.strokeWidth,
      fill: null,
    };
    const spatial = extractSpatialProps('polygon', polyProps);
    sceneApi.addObject({
      id: makeObjectId(),
      type: 'polygon',
      tIn,
      tOut,
      props: polyProps,
      keyframes: spatial ? [{ t: tIn, props: spatial }] : undefined,
    });
    setPolygonPoints(null);
  }, [polygonPoints, currentTime, duration, scene.defaultDuration, scene.color, scene.strokeWidth, sceneApi]);

  useEffect(() => {
    if (!isPolygonTool) return;
    const onKey = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitPolygon();
      } else if (e.key === 'Escape') {
        setPolygonPoints(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPolygonTool, commitPolygon]);

  // ── Drag-to-create ──────────────────────────────────────────────
  const onStageMouseDown = useCallback((e) => {
    if (!isDrawingTool) {
      if (e.target === e.target.getStage()) sceneApi.selectObject(null);
      return;
    }
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (!pos) return;

    // Polygon = click-to-add-point (no drag)
    if (isPolygonTool) {
      const xPct = pos.x / box.w;
      const yPct = pos.y / box.h;
      setPolygonPoints((prev) => {
        const next = prev ? [...prev, [xPct, yPct]] : [[xPct, yPct]];
        return next;
      });
      return;
    }

    setDrag({ tool: scene.activeTool, start: pos, end: pos });
  }, [isDrawingTool, isPolygonTool, scene.activeTool, sceneApi, box.w, box.h]);

  const onStageDblClick = useCallback(() => {
    if (isPolygonTool && polygonPoints && polygonPoints.length >= 3) {
      commitPolygon();
    }
  }, [isPolygonTool, polygonPoints, commitPolygon]);

  const onStageMouseMove = useCallback((e) => {
    if (!drag) return;
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (!pos) return;
    setDrag((d) => (d ? { ...d, end: pos } : d));
  }, [drag]);

  const onStageMouseUp = useCallback(() => {
    if (!drag) return;
    const { tool, start, end } = drag;
    setDrag(null);

    // Ignore tiny drags (treat as click) — except for click-to-place tools
    const dx = Math.abs(end.x - start.x);
    const dy = Math.abs(end.y - start.y);
    const clickToPlaceTools = new Set(['text', 'playerToken']);
    if (!clickToPlaceTools.has(tool) && dx < 4 && dy < 4) return;

    const built = buildObjectFromDrag({
      tool, start, end,
      stageW: box.w, stageH: box.h,
      color: scene.color, strokeWidth: scene.strokeWidth,
    });
    if (!built) return;
    const tIn = Math.max(0, currentTime);
    const tOut = Math.min(
      duration || currentTime + scene.defaultDuration,
      currentTime + scene.defaultDuration
    );
    // Seed keyframes for animatable types
    const spatial = extractSpatialProps(built.type, built.props);
    const keyframes = spatial ? [{ t: tIn, props: spatial }] : undefined;
    sceneApi.addObject({ ...built, id: makeObjectId(), tIn, tOut, keyframes });
  }, [drag, box.w, box.h, scene.color, scene.strokeWidth, scene.defaultDuration, sceneApi, currentTime, duration]);

  // ── Preview object during drag ──────────────────────────────────
  const previewObject = drag && box.w > 0
    ? buildObjectFromDrag({
        tool: drag.tool,
        start: drag.start,
        end: drag.end,
        stageW: box.w,
        stageH: box.h,
        color: scene.color,
        strokeWidth: scene.strokeWidth,
      })
    : null;

  // Objects to render at current time
  const visibleObjects = scene.objects.filter((o) => isObjectVisibleAt(o, currentTime));

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    >
      {box.w > 0 && box.h > 0 && (
        <div
          style={{
            position: 'absolute',
            left: box.left,
            top: box.top,
            width: box.w,
            height: box.h,
            pointerEvents: 'auto',
            cursor: isDrawingTool ? 'crosshair' : 'default',
          }}
        >
          <Stage
            width={box.w}
            height={box.h}
            onMouseDown={onStageMouseDown}
            onMouseMove={onStageMouseMove}
            onMouseUp={onStageMouseUp}
            onMouseLeave={onStageMouseUp}
            onDblClick={onStageDblClick}
          >
            <Layer>
              {visibleObjects.map((obj) => (
                <ShapeRenderer
                  key={obj.id}
                  object={obj}
                  stageW={box.w}
                  stageH={box.h}
                  isSelected={scene.selectedId === obj.id}
                  onSelect={sceneApi.selectObject}
                  onChange={onSpatialChange}
                  draggable={!isDrawingTool}
                  interactive={!isDrawingTool}
                  currentTime={currentTime}
                />
              ))}
              {previewObject && (
                <ShapeRenderer
                  object={previewObject}
                  stageW={box.w}
                  stageH={box.h}
                  isSelected={false}
                  draggable={false}
                  interactive={false}
                  currentTime={currentTime}
                />
              )}

              {/* Polygon-in-progress preview */}
              {isPolygonTool && polygonPoints && polygonPoints.length > 0 && (
                <ShapeRenderer
                  object={{
                    id: '_polyPreview',
                    type: 'polygon',
                    tIn: null, tOut: null,
                    props: {
                      points: polygonPoints,
                      stroke: scene.color,
                      strokeWidth: scene.strokeWidth,
                      fill: null,
                    },
                  }}
                  stageW={box.w}
                  stageH={box.h}
                  isSelected={true}
                  draggable={false}
                  interactive={false}
                />
              )}
            </Layer>
          </Stage>
        </div>
      )}
    </div>
  );
}
